import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Skeleton } from "../components/ui/skeleton";
import { Wallet, TrendingUp, AlertTriangle, Users, Receipt, Clock, Download, RefreshCw, Mail, MessageCircle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid } from "recharts";

const fmtBs = (n) => `Bs. ${(Number(n) || 0).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const AGING_COLORS = { al_dia: "#10B981", "1_30": "#F59E0B", "31_60": "#F97316", "61_90": "#EF4444", mas_90: "#991B1B" };

const KPI = ({ label, value, icon: Icon, tint = "green", hint }) => {
  const tints = { green: "from-green-700 to-emerald-600", orange: "from-orange-600 to-amber-500", red: "from-red-700 to-rose-600", blue: "from-cyan-700 to-sky-600", violet: "from-violet-700 to-purple-600" };
  return (
    <Card className="overflow-hidden border-0 shadow-md">
      <div className={`bg-gradient-to-br ${tints[tint]} p-4 text-white`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-white/80">{label}</p>
            <p className="text-xl md:text-2xl font-extrabold mt-1" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</p>
            {hint && <p className="text-[11px] text-white/70 mt-1">{hint}</p>}
          </div>
          {Icon && <Icon className="w-9 h-9 text-white/40" strokeWidth={1.5} />}
        </div>
      </div>
    </Card>
  );
};

export default function ReporteCuentasPorCobrarPage() {
  const { token } = useAuth();
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ desde: "", hasta: "", clase: "todos", tipo_impuesto: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== "todos") params.append(k, v); });
      const r = await axios.get(`${API}/reportes/cuentas-por-cobrar?${params}`, auth);
      setData(r.data);
    } catch (e) { toast.error("Error: " + (e.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  }, [filters, auth]);

  useEffect(() => { load(); }, [load]);

  const exportarCSV = () => {
    if (!data) return;
    const rows = [
      ["Categoría", "Cantidad", "Monto (Bs.)"],
      ...data.aging.map(a => [a.label, a.cantidad, a.monto]),
      [],
      ["TOP DEUDORES", "", ""],
      ["Contribuyente", "Planillas", "Monto (Bs.)"],
      ...data.top_deudores.map(d => [d.nombre, d.planillas, d.monto_adeudado]),
      [],
      ["POR PERIODO", "", ""],
      ...data.por_periodo.map(p => [p.periodo, p.cantidad, p.monto]),
    ];
    const csv = rows.map(r => r.map(x => `"${String(x ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `cuentas-por-cobrar-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV descargado");
  };

  if (loading && !data) return <div className="p-8 space-y-4"><Skeleton className="h-32" /><Skeleton className="h-64" /></div>;
  if (!data) return null;

  const { totales, aging, por_periodo, por_tipo_impuesto, top_deudores } = data;
  const pctVencido = totales.monto_por_cobrar ? (totales.monto_vencido / totales.monto_por_cobrar) * 100 : 0;

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="page-cuentas-por-cobrar">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-green-900 via-emerald-800 to-lime-700 p-6 text-white shadow-xl">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 50%, white 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
        <div className="relative flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-white/15 rounded-2xl flex items-center justify-center backdrop-blur-sm">
              <Wallet className="w-8 h-8 text-orange-300" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-orange-200/90">Reporte financiero</p>
              <h1 className="font-display font-extrabold text-2xl md:text-3xl mt-1">Cuentas por Cobrar</h1>
              <p className="text-xs text-white/70 mt-1">Cuánto dinero está por ingresar a las cuentas recaudadoras</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={load} data-testid="btn-refresh"><RefreshCw className="w-3 h-3 mr-1" />Refrescar</Button>
            <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white" onClick={exportarCSV} data-testid="btn-export-csv"><Download className="w-3 h-3 mr-1" />Exportar CSV</Button>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div><Label className="text-xs">Desde emisión</Label><Input type="date" value={filters.desde} onChange={e => setFilters(f => ({ ...f, desde: e.target.value }))} data-testid="f-desde" /></div>
          <div><Label className="text-xs">Hasta emisión</Label><Input type="date" value={filters.hasta} onChange={e => setFilters(f => ({ ...f, hasta: e.target.value }))} data-testid="f-hasta" /></div>
          <div>
            <Label className="text-xs">Clase</Label>
            <Select value={filters.clase} onValueChange={v => setFilters(f => ({ ...f, clase: v }))}>
              <SelectTrigger data-testid="f-clase"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="natural">Persona Natural</SelectItem>
                <SelectItem value="juridico">Persona Jurídica</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Tipo impuesto (contiene)</Label><Input value={filters.tipo_impuesto} onChange={e => setFilters(f => ({ ...f, tipo_impuesto: e.target.value }))} placeholder="ej: aseo" data-testid="f-tipo" /></div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Monto por Cobrar" value={fmtBs(totales.monto_por_cobrar)} icon={Wallet} tint="green" hint={`${totales.planillas_pendientes} planillas pendientes`} />
        <KPI label="Monto Vencido" value={fmtBs(totales.monto_vencido)} icon={AlertTriangle} tint="red" hint={`${pctVencido.toFixed(1)}% del total · ${totales.planillas_vencidas} planillas`} />
        <KPI label="Al Día" value={fmtBs(totales.monto_al_dia)} icon={TrendingUp} tint="blue" hint={`${totales.planillas_al_dia} planillas dentro de plazo`} />
        <KPI label="Deudores Únicos" value={totales.contribuyentes_deudores.toLocaleString("es-VE")} icon={Users} tint="violet" hint={`Ticket promedio: ${fmtBs(totales.ticket_promedio)}`} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Aging bucket chart */}
        <Card>
          <CardHeader><CardTitle className="text-sm text-green-900 flex items-center gap-2"><Clock className="w-4 h-4" />Antigüedad de la deuda</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={aging}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="label" fontSize={10} angle={-15} textAnchor="end" height={60} />
                <YAxis fontSize={10} tickFormatter={v => v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(0)}K` : v} />
                <Tooltip formatter={v => fmtBs(v)} />
                <Bar dataKey="monto">
                  {aging.map((a, i) => <Cell key={i} fill={AGING_COLORS[a.key] || "#666"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        {/* Pie por tipo de impuesto */}
        <Card>
          <CardHeader><CardTitle className="text-sm text-green-900 flex items-center gap-2"><Receipt className="w-4 h-4" />Por tipo de impuesto</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={por_tipo_impuesto} dataKey="monto" nameKey="tipo" outerRadius={90} label={e => `${e.tipo}: ${fmtBs(e.monto)}`}>
                  {por_tipo_impuesto.map((_, i) => <Cell key={i} fill={["#14532D", "#EA580C", "#65A30D", "#0891B2", "#7C3AED"][i % 5]} />)}
                </Pie>
                <Tooltip formatter={v => fmtBs(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Aging table detail */}
      <Card>
        <CardHeader><CardTitle className="text-sm text-green-900">Detalle de antigüedad</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow className="bg-green-50">
              <TableHead>Rango</TableHead>
              <TableHead className="text-right">Planillas</TableHead>
              <TableHead className="text-right">Monto (Bs.)</TableHead>
              <TableHead className="text-right">% del total</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {aging.map(a => (
                <TableRow key={a.key} data-testid={`aging-${a.key}`}>
                  <TableCell><span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: AGING_COLORS[a.key] }} />{a.label}</span></TableCell>
                  <TableCell className="text-right font-mono">{a.cantidad}</TableCell>
                  <TableCell className="text-right font-mono font-bold">{fmtBs(a.monto)}</TableCell>
                  <TableCell className="text-right text-slate-600 text-xs">{totales.monto_por_cobrar ? ((a.monto / totales.monto_por_cobrar) * 100).toFixed(1) : "0.0"}%</TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-green-50 font-bold">
                <TableCell>TOTAL</TableCell>
                <TableCell className="text-right font-mono">{totales.planillas_pendientes}</TableCell>
                <TableCell className="text-right font-mono text-orange-700">{fmtBs(totales.monto_por_cobrar)}</TableCell>
                <TableCell className="text-right">100.0%</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Top deudores */}
      <Card>
        <CardHeader><CardTitle className="text-sm text-green-900 flex items-center gap-2"><Users className="w-4 h-4" />Top 20 deudores</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow className="bg-green-50">
              <TableHead className="w-8">#</TableHead>
              <TableHead>Contribuyente</TableHead>
              <TableHead>RIF</TableHead>
              <TableHead className="text-right">Planillas</TableHead>
              <TableHead className="text-right">Monto adeudado</TableHead>
              <TableHead className="text-center">Contacto</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {top_deudores.map((d, i) => (
                <TableRow key={d.contribuyente_id} data-testid={`deudor-${i}`}>
                  <TableCell className="text-xs text-slate-500 font-mono">{i + 1}</TableCell>
                  <TableCell className="font-medium text-sm">{d.nombre}</TableCell>
                  <TableCell className="text-xs font-mono">{d.rif}</TableCell>
                  <TableCell className="text-right font-mono">{d.planillas}</TableCell>
                  <TableCell className="text-right font-mono font-bold text-orange-700">{fmtBs(d.monto_adeudado)}</TableCell>
                  <TableCell className="text-center">
                    <div className="flex justify-center gap-1">
                      {d.email && <a href={`mailto:${d.email}`} title={d.email}><Mail className="w-3.5 h-3.5 text-cyan-600 hover:text-cyan-800" /></a>}
                      {d.telefono && <a href={`https://wa.me/${d.telefono.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" title={d.telefono}><MessageCircle className="w-3.5 h-3.5 text-emerald-600 hover:text-emerald-800" /></a>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {top_deudores.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Sin planillas pendientes con los filtros actuales</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Por período */}
      {por_periodo.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-sm text-green-900">Deuda por período</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow className="bg-green-50"><TableHead>Período</TableHead><TableHead className="text-right">Planillas</TableHead><TableHead className="text-right">Monto (Bs.)</TableHead></TableRow></TableHeader>
              <TableBody>
                {por_periodo.map(p => (
                  <TableRow key={p.periodo}>
                    <TableCell className="font-mono">{p.periodo}</TableCell>
                    <TableCell className="text-right font-mono">{p.cantidad}</TableCell>
                    <TableCell className="text-right font-mono font-bold">{fmtBs(p.monto)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
